import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./AuthContext";
import useNotification from "../hooks/useNotification";
import { socket } from "../utils/socket";
import { requestPushPermission, showPush } from "../utils/pushNotification";

export const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const { isLogged, user } = useContext(AuthContext);
    const { getNotifications } = useNotification();

    // ✅ jamais undefined (sinon filter casse)
    const [notifications, setNotifications] = useState([]);

    // 1) charger au démarrage
    useEffect(() => {
        if (!isLogged || !user?._id) {
            setNotifications([]);
            return;
        }

        getNotifications()
            .then((res) => setNotifications(res?.data?.data || []))
            .catch(() => setNotifications([]));
    }, [isLogged, user?._id]);

    const onReadAll = ({ userId }) => {
        // si jamais tu reçois pour un autre user
        if (String(userId) !== String(user?._id)) return;

        setNotifications((prev) =>
            prev.map((n) =>
                String(n.id_utilisateur) === String(userId) ? { ...n, vu: 1 } : n
            )
        );
    };


    // 2) socket realtime
    useEffect(() => {
        if (!isLogged || !user?._id) {
            socket.disconnect();
            return;
        }

        requestPushPermission();

        if (!socket.connected) socket.connect();

        socket.emit("joinUserRoom", user._id);

        const onNewNotification = (notif) => {
            if (String(notif.id_utilisateur) !== String(user._id)) return;

            setNotifications((prev) => [notif, ...prev]);

            const isOnNotificationsPage =
                window.location.pathname.startsWith("/notification");

            if (!isOnNotificationsPage) {
                showPush(notif);
            }
        };


        const onReadOne = ({ notificationId }) => {
            setNotifications((prev) =>
                prev.map((n) =>
                    String(n._id) === String(notificationId) ? { ...n, vu: 1 } : n
                )
            );
        };

        socket.on("notification:new", onNewNotification);
        socket.on("notification:readOne", onReadOne);
        socket.on("notification:readAll", onReadAll);

        return () => {
            socket.off("notification:new", onNewNotification);
            socket.off("notification:readOne", onReadOne);
            socket.off("notification:readAll", onReadAll);
        };
    }, [isLogged, user?._id]);

    const unreadCount = useMemo(() => {
        return notifications.filter(
            (n) => String(n.id_utilisateur) === String(user?._id) && n.vu === 2
        ).length;
    }, [notifications, user?._id]);

    const value = useMemo(
        () => ({ notifications, setNotifications, unreadCount }),
        [notifications, unreadCount]
    );

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationRealtime = () => useContext(NotificationContext);
